import houseAdsJson from "../data/generated/houseAds.json";
import type { HouseAdCampaign, HouseAdType } from "@yjsgames/house-ads";

/**
 * 하우스 광고 캠페인 데이터 (houseAds.tsv → generated/houseAds.json 재구성, dailyShopData 동형).
 * 에셋 경로/텍스처 키/YouTube embed URL 부여만 앱 책임 — 선택(가중 추첨)·타입·클릭분기는 @yjsgames/house-ads(순수)가 담당.
 *
 * 에셋: image=`public/ads/<assetKey>.png`, video=`.mp4`, youtube=`assetKey`를 YouTube video id로 해석.
 * 전용 에셋 준비 시 houseAds.tsv만 교체(no-hardcode).
 */

/** public/ads/ 상대 경로 prefix — 루트 "/" 금지(itch 등 서브경로 배포 404 방지). 테마 BASE와 동일 규약. */
const ADS_DIR = "ads/";

/**
 * 모바일에서 자사(하우스) 광고를 노출할 확률 (나머지는 네이티브 LevelPlay).
 * 웹 기본은 house, CrazyGames 빌드는 SDK rewarded가 우선된다. (RoutingRewardedAdProvider)
 */
export const HOUSE_AD_MOBILE_SHARE = 0.1;

/** generated/houseAds.json → HouseAdCampaign[] (데이터 SSoT). */
export const HOUSE_AD_CAMPAIGNS: readonly HouseAdCampaign[] = houseAdsJson.map((row) => ({
  campaignId: row.campaignId,
  adType: row.adType as HouseAdType,
  assetKey: row.assetKey,
  clickUrl: row.clickUrl,
  clickUrlIos: (row as { clickUrlIos?: string }).clickUrlIos || undefined,
  clickUrlAndroid: (row as { clickUrlAndroid?: string }).clickUrlAndroid || undefined,
  titleKey: row.titleKey,
  descKey: (row as { descKey?: string }).descKey || undefined,
  ctaKey: row.ctaKey,
  rewardSeconds: row.rewardSeconds,
  volume: (row as { volume?: number }).volume,
  weight: row.weight,
  enable: row.enable,
}));

/** image 캠페인의 Phaser 텍스처 키 — BootScene이 이 키로 load.image, 모달이 이 키로 표시. */
export function houseAdTextureKey(campaign: HouseAdCampaign): string {
  return `housead_${campaign.campaignId}`;
}

/**
 * 에셋 URL — image=png(텍스처), video=mp4(DOM video src), youtube=embed iframe src.
 * youtube는 자동재생·무음·인라인·컨트롤숨김으로 보상형 광고 영역에 맞춰 임베드한다.
 */
export function houseAdAssetUrl(campaign: HouseAdCampaign): string {
  if (campaign.adType === "youtube") {
    return `https://www.youtube.com/embed/${campaign.assetKey}?autoplay=1&mute=1&playsinline=1&controls=0&rel=0&enablejsapi=1`;
  }
  const ext = campaign.adType === "video" ? "mp4" : "png";
  return `${ADS_DIR}${campaign.assetKey}.${ext}`;
}

/** BootScene preload 대상 — 활성 image 캠페인만 (video=DOM, youtube=iframe이라 preload 불필요). */
export function houseAdImagePreloads(): readonly { readonly key: string; readonly url: string }[] {
  return HOUSE_AD_CAMPAIGNS.filter((c) => c.enable && c.adType === "image").map((c) => ({
    key: houseAdTextureKey(c),
    url: houseAdAssetUrl(c),
  }));
}
